import { useState, useEffect, useCallback } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { useNavigate, Link } from 'react-router-dom';
import { MdArrowUpward, MdArrowDownward, MdClose, MdInfoOutline, MdAlbum } from 'react-icons/md';
import '../../Styles/ArtistRoot.css';
import '../../Styles/Admin.css';
import GENRES from '../../utils/Genres';
import izenuAPI from '../../services/izenuAPI';
import UpcQuestion, { upcProblem, upcPayload } from './UpcQuestion';

const ARTWORK_MAX_MB = 10;
const KIND = { album: 'álbum', ep: 'EP' };

const muiSx = {
    '& .MuiOutlinedInput-root': {
        backgroundColor: '#fff',
        borderRadius: '0.5rem',
        '& fieldset': { borderColor: '#D1D5DB' },
        '&:hover fieldset': { borderColor: '#F59E0B' },
        '&.Mui-focused fieldset': { borderColor: '#F59E0B' },
    },
};
const inputCls = 'block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-amber-500 focus:ring-amber-500';

function formatDuration(ms) {
    if (!ms) return '';
    const totalSec = Math.floor(Number(ms) / 1000);
    return `${Math.floor(totalSec / 60)}:${String(totalSec % 60).padStart(2, '0')}`;
}

function Cover({ src, size = 40 }) {
    return src
        ? <img src={src} alt="" loading="lazy" style={{ width: size, height: size }} className="shrink-0 rounded object-cover" />
        : <div style={{ width: size, height: size }} className="shrink-0 rounded bg-gray-200" />;
}

export default function MergeSingles() {
    const navigate = useNavigate();
    const distributorId = localStorage.getItem('id');

    const [artists, setArtists] = useState([]);
    const [selectedArtist, setSelectedArtist] = useState(null);

    const [singles, setSingles] = useState([]);
    const [loadingSingles, setLoadingSingles] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]); // ordered music_ids
    const [filter, setFilter] = useState('');

    const [albumTitle, setAlbumTitle] = useState('');
    const [albumType, setAlbumType] = useState('album');
    const [albumGenre, setAlbumGenre] = useState('');
    const [upcMode, setUpcMode] = useState('');
    const [upc, setUpc] = useState('');
    const [genreTouched, setGenreTouched] = useState(false);
    const [artworkFile, setArtworkFile] = useState(null);
    const [artworkPreview, setArtworkPreview] = useState(null);
    const [artworkError, setArtworkError] = useState('');
    // 'keep': the album links the tracks and the singles stay (as stores do).
    // 'convert': the tracks move and empty singles go. No default on purpose.
    const [mergeMode, setMergeMode] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        izenuAPI.get('/distributor/artist/fetchArtists.php?distributor_id=' + distributorId)
            .then(({ data: r }) => setArtists(r.artists || []))
            .catch(err => console.log(err));
    }, [distributorId]);

    useEffect(() => () => { if (artworkPreview) URL.revokeObjectURL(artworkPreview); }, [artworkPreview]);

    const fetchSingles = useCallback(async (artistId) => {
        setLoadingSingles(true);
        setSingles([]);
        setSelectedIds([]);
        try {
            const { data: r } = await izenuAPI.get('/distributor/releases/listSingles.php?artist_id=' + artistId);
            if (r.response && r.singles) setSingles(r.singles);
        } catch (err) {
            console.log(err);
        }
        setLoadingSingles(false);
    }, []);

    const toggleSelect = useCallback((musicId) => {
        setSelectedIds(prev => prev.includes(musicId) ? prev.filter(i => i !== musicId) : [...prev, musicId]);
    }, []);

    const moveTrack = useCallback((musicId, direction) => {
        setSelectedIds(prev => {
            const idx = prev.indexOf(musicId);
            const newIdx = idx + direction;
            if (idx < 0 || newIdx < 0 || newIdx >= prev.length) return prev;
            const arr = [...prev];
            [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
            return arr;
        });
    }, []);

    const selectedSingles = selectedIds.map(i => singles.find(s => s.music_id === i)).filter(Boolean);

    // Suggest the most common genre of the chosen tracks, following the
    // selection until the user picks one by hand.
    useEffect(() => {
        if (genreTouched) return;
        const counts = {};
        selectedSingles.forEach(s => { if (s.genre) counts[s.genre] = (counts[s.genre] || 0) + 1; });
        const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
        setAlbumGenre(top ? top[0] : '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIds, genreTouched]);

    const handleArtwork = (e) => {
        const file = e.target.files[0];
        e.target.value = '';
        setArtworkError('');
        if (!file) return;
        if (!['jpg', 'jpeg', 'png', 'webp'].includes((file.name.split('.').pop() || '').toLowerCase())) {
            setArtworkError('Formato não suportado. Usa JPG, PNG ou WEBP.');
            return;
        }
        if (file.size > ARTWORK_MAX_MB * 1024 * 1024) {
            setArtworkError(`A imagem tem ${(file.size / 1024 / 1024).toFixed(1)} MB. O máximo é ${ARTWORK_MAX_MB} MB.`);
            return;
        }
        setArtworkFile(file);
        setArtworkPreview(URL.createObjectURL(file));
    };

    const canSubmit = selectedArtist && albumTitle.trim() && albumGenre && mergeMode && !upcProblem(upcMode, upc) && selectedIds.length >= 2 && !submitting;

    const handleMerge = useCallback(async () => {
        if (!selectedArtist || !albumTitle.trim() || selectedIds.length < 2) return;
        setSubmitting(true);
        try {
            const { data: r } = await izenuAPI.post('/distributor/releases/mergeSingles.php', {
                artist_id: selectedArtist.user_id,
                title: albumTitle.trim(),
                type: albumType,
                genre: albumGenre,
                ...upcPayload(upcMode, upc),
                music_ids: selectedIds,
                keep_singles: mergeMode === 'keep',
                delete_empty_singles: mergeMode === 'convert'
            });
            if (!r.response) {
                alert(r.message || 'Não foi possível criar o ' + KIND[albumType] + '.');
                setSubmitting(false);
                return;
            }
            if (artworkFile && r.release_id) {
                try {
                    const formData = new FormData();
                    formData.append('artwork', artworkFile);
                    formData.append('release_id', r.release_id);
                    formData.append('artist_id', selectedArtist.user_id);
                    await izenuAPI.post('/distributor/releases/uploadArtwork.php', formData);
                } catch (artErr) {
                    console.log('Artwork upload failed:', artErr);
                    alert(`O ${KIND[albumType]} foi criado, mas não foi possível enviar a capa. Podes enviá-la na página dele.`);
                }
            }
            navigate('/release/' + r.release_id);
        } catch (err) {
            console.log(err);
            alert(err?.response?.data?.message || 'Não foi possível criar o ' + KIND[albumType] + '. Tenta novamente.');
        }
        setSubmitting(false);
    }, [selectedArtist, albumTitle, albumType, albumGenre, upcMode, upc, selectedIds, mergeMode, artworkFile, navigate]);

    const visibleSingles = singles.filter(s => !filter.trim() || (s.title || '').toLowerCase().includes(filter.toLowerCase()));
    const kind = KIND[albumType];

    return (
        <div className="mx-auto max-w-5xl space-y-6 p-3 sm:p-6 text-left">
            <Link to="/releases" className="inline-block text-sm text-amber-700 hover:underline">← Álbuns / EPs</Link>
            <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Agrupar singles num álbum ou EP</h1>
                <p className="text-sm text-gray-500">Escolhe músicas que já carregaste como singles e junta-as num álbum ou EP novo, pela ordem que quiseres.</p>
            </div>

            <div className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
                <MdInfoOutline size={18} className="mt-0.5 shrink-0" aria-hidden="true" />
                <p>
                    No fim escolhes se os singles <strong>continuam publicados</strong> ao lado do {kind} ou se as faixas <strong>mudam-se</strong> para ele.
                    Em ambos os casos as músicas mantêm as reproduções e o estado. Para juntar músicas a um álbum que já existe, usa
                    "Adicionar existente" na página desse álbum, em <Link to="/releases" className="font-medium underline">Álbuns / EPs</Link>.
                </p>
            </div>

            {/* 1. Artist */}
            <section className="rounded-xl border border-gray-200 bg-white p-4 sm:p-6">
                <h2 className="mb-4 text-lg font-semibold text-gray-900">1. Artista</h2>
                <Autocomplete
                    options={artists}
                    getOptionLabel={(o) => o.artist_name || o.name || ''}
                    isOptionEqualToValue={(o, v) => o?.user_id === v?.user_id}
                    value={selectedArtist}
                    onChange={(event, newValue) => {
                        setSelectedArtist(newValue);
                        setSingles([]);
                        setSelectedIds([]);
                        setAlbumTitle('');
                        setAlbumGenre('');
                        setUpcMode('');
                        setUpc('');
                        setGenreTouched(false);
                        setFilter('');
                        if (newValue) fetchSingles(newValue.user_id);
                    }}
                    renderInput={(params) => <TextField {...params} label="Artista" size="small" sx={muiSx} />}
                />
            </section>

            {/* 2. Tracks */}
            {selectedArtist && (
                <section className="rounded-xl border border-gray-200 bg-white">
                    <div className="flex flex-wrap items-center gap-2 border-b px-4 py-3">
                        <h2 className="flex-1 text-lg font-semibold text-gray-900">2. Músicas <span className="text-sm font-normal text-gray-500">{selectedIds.length} de {singles.length} escolhidas</span></h2>
                        {singles.length > 0 && (
                            <div className="flex gap-2">
                                <button type="button" onClick={() => setSelectedIds(singles.map(s => s.music_id))}
                                    className="rounded-lg !border !border-solid border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">Escolher todas</button>
                                <button type="button" onClick={() => setSelectedIds([])} disabled={selectedIds.length === 0}
                                    className="rounded-lg !border !border-solid border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40">Limpar</button>
                            </div>
                        )}
                    </div>

                    {loadingSingles ? (
                        <p className="py-10 text-center text-sm text-gray-500">A carregar singles…</p>
                    ) : singles.length === 0 ? (
                        <p className="py-10 text-center text-sm text-gray-500">Este artista não tem singles que possam ser agrupados.</p>
                    ) : (
                        <>
                            {singles.length > 8 && (
                                <div className="border-b px-4 py-3">
                                    <div className="search-distributors-input" style={{ maxWidth: 'none' }}>
                                        <input type="search" placeholder="Filtrar por título…" value={filter} onChange={(e) => setFilter(e.target.value)} style={{ padding: '6px 0', fontSize: 14 }} />
                                    </div>
                                </div>
                            )}
                            <ul className="max-h-[420px] divide-y divide-gray-100 overflow-y-auto [&>li]:!p-0">
                                {visibleSingles.map((s) => {
                                    const pos = selectedIds.indexOf(s.music_id);
                                    const isSelected = pos >= 0;
                                    return (
                                        <li key={s.music_id}>
                                            <label className={`flex cursor-pointer items-center gap-3 px-4 py-2.5 ${isSelected ? 'bg-amber-50' : 'hover:bg-gray-50'}`}>
                                                <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(s.music_id)}
                                                    className="h-4 w-4 shrink-0 rounded border-gray-300 text-amber-500 focus:ring-amber-500" />
                                                <Cover src={s.cover} />
                                                <span className="min-w-0 flex-1">
                                                    <span className="block truncate text-sm font-medium text-gray-900">
                                                        {s.title}
                                                        {Number(s.explicit) === 1 && <span title="Conteúdo explícito" style={{ display: 'inline-block', marginLeft: 6, padding: '0 5px', borderRadius: 3, background: '#4b5563', color: '#ffffff', fontSize: 10, fontWeight: 700, lineHeight: '16px', verticalAlign: 'middle' }}>E</span>}
                                                    </span>
                                                    <span className="block truncate text-xs text-gray-500">
                                                        {[s.genre, formatDuration(s.duration), `${Number(s.plays || 0).toLocaleString('pt-PT')} plays`].filter(Boolean).join(' · ')}
                                                    </span>
                                                </span>
                                                {isSelected && <span className="shrink-0 rounded-full bg-amber-500 px-2 py-0.5 text-xs font-bold !text-white">#{pos + 1}</span>}
                                            </label>
                                        </li>
                                    );
                                })}
                                {visibleSingles.length === 0 && <li><p className="py-6 text-center text-sm text-gray-500">Nenhuma música com esse título.</p></li>}
                            </ul>
                        </>
                    )}
                </section>
            )}

            {/* 3. Order and details */}
            {selectedArtist && singles.length > 0 && (
                <section className="rounded-xl border border-gray-200 bg-white p-4 sm:p-6">
                    <h2 className="mb-4 text-lg font-semibold text-gray-900">3. Ordem e dados do {kind}</h2>

                    {selectedSingles.length === 0 ? (
                        <p className="mb-4 rounded-lg bg-gray-50 px-3 py-4 text-center text-sm text-gray-500">Escolhe pelo menos 2 músicas acima.</p>
                    ) : (
                        <ol className="mb-5 divide-y divide-amber-100 rounded-lg border border-amber-200 bg-amber-50/50 [&>li]:!p-0">
                            {selectedSingles.map((s, idx) => (
                                <li key={s.music_id}>
                                    <div className="flex items-center gap-2 px-3 py-2">
                                        <span className="w-6 shrink-0 text-center text-sm font-bold text-amber-700">{idx + 1}</span>
                                        <Cover src={s.cover} size={32} />
                                        <span className="min-w-0 flex-1 truncate text-sm text-gray-900">{s.title}</span>
                                        <button type="button" onClick={() => moveTrack(s.music_id, -1)} disabled={idx === 0} aria-label="Subir"
                                            className="rounded p-1 text-gray-500 hover:bg-white disabled:text-gray-300"><MdArrowUpward size={16} /></button>
                                        <button type="button" onClick={() => moveTrack(s.music_id, 1)} disabled={idx === selectedSingles.length - 1} aria-label="Descer"
                                            className="rounded p-1 text-gray-500 hover:bg-white disabled:text-gray-300"><MdArrowDownward size={16} /></button>
                                        <button type="button" onClick={() => toggleSelect(s.music_id)} aria-label="Tirar"
                                            className="rounded p-1 text-gray-400 hover:bg-white hover:text-red-600"><MdClose size={16} /></button>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    )}

                    <div className="flex flex-col gap-5 sm:flex-row">
                        <div className="shrink-0 sm:w-40">
                            <span className="mb-1 block text-sm font-medium text-gray-700">Capa</span>
                            <label className="relative flex h-32 w-32 cursor-pointer items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 hover:border-amber-400">
                                {artworkPreview
                                    ? <img src={artworkPreview} alt="" className="h-full w-full object-cover" />
                                    : <span className="flex flex-col items-center gap-1 text-xs text-gray-500"><MdAlbum size={28} className="text-gray-400" />Escolher capa</span>}
                                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleArtwork} className="sr-only" />
                            </label>
                            <p className="mt-1 text-xs text-gray-500">JPG, PNG ou WEBP · quadrada, 3000 × 3000 px · até {ARTWORK_MAX_MB} MB</p>
                            {artworkError && <p className="mt-1 text-xs text-red-600">{artworkError}</p>}
                        </div>
                        <div className="grid min-w-0 flex-1 grid-cols-1 content-start gap-3 sm:grid-cols-2">
                            <label className="block sm:col-span-2">
                                <span className="mb-1 block text-sm font-medium text-gray-700">Título *</span>
                                <input className={inputCls} value={albumTitle} onChange={(e) => setAlbumTitle(e.target.value)} placeholder="Nome do álbum ou EP" />
                            </label>
                            <label className="block">
                                <span className="mb-1 block text-sm font-medium text-gray-700">Tipo</span>
                                <select className={inputCls} value={albumType} onChange={(e) => setAlbumType(e.target.value)}>
                                    <option value="album">Álbum</option>
                                    <option value="ep">EP</option>
                                </select>
                            </label>
                            <label className="block">
                                <span className="mb-1 block text-sm font-medium text-gray-700">Género *</span>
                                <select className={inputCls} value={albumGenre} onChange={(e) => { setGenreTouched(true); setAlbumGenre(e.target.value); }}>
                                    <option value="" disabled>Escolhe o género</option>
                                    {GENRES.map(g => <option key={g.name} value={g.name}>{g.name}</option>)}
                                </select>
                            </label>
                            <div className="sm:col-span-2">
                                <UpcQuestion mode={upcMode} upc={upc} onModeChange={setUpcMode} onUpcChange={setUpc} showError={upcMode === 'existing'} />
                            </div>
                            <fieldset className="sm:col-span-2">
                                <legend className="mb-2 text-sm font-medium text-gray-700">O que fazer com os singles de onde vêm as faixas? <span className="text-red-500">*</span></legend>
                                <div className="space-y-2">
                                    {[
                                        ['keep', 'Manter também como singles',
                                            `O ${kind} inclui as faixas e os singles continuam publicados, como nas lojas. É a mesma gravação: as reproduções e os royalties não se dividem. Usa isto para singles que já saíram antes do ${kind}.`],
                                        ['convert', `Transformar em ${kind}`,
                                            `As faixas passam para o ${kind} e os singles vazios são apagados. Usa isto para arrumar singles que nunca foram lançamentos à parte (por exemplo, criados automaticamente ao carregar). Não se aplica a singles à venda ou já comprados.`],
                                    ].map(([value, label, hint]) => (
                                        <label key={value} className={`flex cursor-pointer items-start gap-3 rounded-lg !border !border-solid p-3 ${mergeMode === value ? 'border-amber-400 bg-amber-50/60' : 'border-gray-200 bg-white hover:bg-gray-50'}`}>
                                            <input type="radio" name="merge_mode" value={value} checked={mergeMode === value}
                                                onChange={() => setMergeMode(value)} className="mt-0.5 accent-amber-500" />
                                            <span className="text-sm text-gray-800">
                                                <strong className="font-medium">{label}</strong>
                                                <span className="block text-xs text-gray-500">{hint}</span>
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </fieldset>
                        </div>
                    </div>

                    <div className="mt-5 flex justify-end border-t pt-4">
                        <button type="button" onClick={handleMerge} disabled={!canSubmit}
                            className="w-full rounded-lg bg-amber-500 px-6 py-3 text-sm font-semibold !text-white hover:bg-amber-600 disabled:opacity-50 sm:w-auto">
                            {submitting ? 'A criar…' : `Criar ${kind} com ${selectedIds.length} ${selectedIds.length === 1 ? 'faixa' : 'faixas'}`}
                        </button>
                    </div>
                </section>
            )}
        </div>
    );
}
